import React, { useEffect, useState } from "react";
import SplitPane from "react-split-pane";
import { useParams } from "react-router";
import { FaEdit } from "react-icons/fa";
import { CiSearch } from "react-icons/ci";
import { FaTrash } from "react-icons/fa6";
import { Form, Table } from "react-bootstrap";
import { cdiscManagementService } from "../../../APIServices/CDISC/cdiscManagementService";
import {
  NoDataFoundTable,
  PageLoader,
} from "../../../Components/NoDataFoundTable/NoDataFoundTable";
import Swal from "sweetalert2";
import Moment from "react-moment";
import Pagination from "../../Pagination";
import UseToggle from "../../../Hooks/UseToggle";
import PullTMapMetadata from "./PullTMapMetadata";
import AddNewTMapMetadata from "./AddNewTMapMetadata";
import UpdateTMapMetadata from "./UpdateTMapMetadata";
import DeletedTMapMetadata from "./DeletedTMapMetadata";
import TableProperties from "./Properties/TableProperties";
import TMapMetadataActivities from "./Properties/TMapMetadataActivities";
import { studyOnboardingService } from "../../../APIServices/studyOnboarding";
import { studyOtherSettingsServices } from "../../../APIServices/studyOtherSettingsServices";
import ImportTMapData from "./Properties/ImportTMapData";

const TMapManagement = ({ onMouseDown, tableRef }) => {
  const toggles = UseToggle();
  const { studyId } = useParams();
  const [loader, setLoader] = useState(false);
  const [searchData, setSearchData] = useState("");
  const [tMapMetadata, setTMapMetadata] = useState([]);
  const [tMapMetadataCount, setTMapMetadataCount] = useState(0);
  const [tMapData, setTMapData] = useState("");
  const [isFormOpen, setIsFormOpen] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [tMapId, setTMapId] = useState("");
  const [activeTab, setActiveTab] = useState("activities");
  const [tableColumns, setTableColumns] = useState({});
  const StudyMode = JSON.parse(localStorage.getItem("StudyMode"));

  const formColumn = [
    {
      columnLabel: "Domain",
      columnName: "DOMAIN",
      columnType: "text",
    },
    {
      columnLabel: "Pattern",
      columnName: "PATTERN",
      columnType: "text",
    },
    {
      columnLabel: "Test CD",
      columnName: "TESTCD",
      columnType: "text",
    },
    {
      columnLabel: "Test Name",
      columnName: "TESTNAME",
      columnType: "text",
    },
    {
      columnLabel: "Created Date | Time",
      columnName: "CREATED",
      columnType: "date & time",
    },
    {
      columnLabel: "Updated Date | Time",
      columnName: "UPDATED",
      columnType: "date & time",
    },
    { columnLabel: "Edit", columnName: "EDIT", columnType: "action" },
    { columnLabel: "Delete", columnName: "DELETE", columnType: "action" },
  ];

  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const startIndex = (pageNumber - 1) * itemsPerPage;
  const totalPage = Math.ceil(tMapMetadataCount / itemsPerPage);

  const getStudyCDISCTMap = async () => {
    setLoader(true);
    let data = await cdiscManagementService.getStudyCDISCTMap(
      "active",
      studyId,
      searchData,
      pageNumber,
      itemsPerPage
    );
    setTMapMetadata(data?.data || []);
    setTMapMetadataCount(data?.count || 0);
    setLoader(false);
  };

  const downloadTMapTemplate = async () => {
    let data = await cdiscManagementService.downloadTMapTemplate();
    const csvContent = (data?.data || []).join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "TMap-Template.csv";
    link.click();
  };

  useEffect(() => {
    getStudyCDISCTMap();
  }, [studyId, searchData, pageNumber, itemsPerPage]);

  const updateTMapModal = (item) => {
    setTMapData(item);
    toggles.setToggle();
  };

  const [pageNumberDeleted, setPageNumberDeleted] = useState(1);
  const [itemsPerPageDeleted, setItemsPerPageDeleted] = useState(10);
  const [deletedTMapCount, setDeletedTMapCount] = useState(0);
  const [deletedTMap, setDeletedTMap] = useState([]);

  const getDeletedTMapList = async () => {
    let data = await cdiscManagementService.getStudyCDISCTMap(
      "deleted",
      studyId,
      "",
      pageNumberDeleted,
      itemsPerPageDeleted
    );
    setDeletedTMap(data?.data || []);
    setDeletedTMapCount(data?.count || 0);
  };

  const softDeleteRestoreStudyCDISCTMaps = async (id, type) => {
    Swal.fire({
      title: "Are you sure?",
      text: `You want to ${type} tmaps!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#103C5E",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${type} it!`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        let userData = {};
        userData.id = id;
        userData.studyId = studyId;
        userData.type = type;
        let data =
          await cdiscManagementService.softDeleteRestoreStudyCDISCTMaps(
            userData
          );
        if (data?.statusCode === 200) {
          getStudyCDISCTMap();
          getDeletedTMapList();
          setTMapId("");
          if (type === "restore") {
            getDeletedTMapList(pageNumberDeleted, itemsPerPageDeleted);
            toggles.setToggle1();
          }
        }
      }
    });
  };

  const [formActivity, setFormActivity] = useState([]);
  const [formActivityCount, setFormActivityCount] = useState(0);
  const [activityLoader, setActivityLoader] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);

  const getStudyCDISCAuditTrials = async (pageNumber, itemsPerPage) => {
    setActivityLoader(true);
    let data = await studyOnboardingService.getStudyCDISCAuditTrials(
      "CDISCTMapMetadata",
      studyId,
      tMapData?.tMapId,
      StudyMode,
      pageNumber,
      itemsPerPage
    );
    setFormActivity(data?.data || []);
    setFormActivityCount(data?.count || 0);
    setActivityLoader(false);
  };

  const getGlobalTablePropertiesData = async (e) => {
    let data = await studyOtherSettingsServices.getGlobalTablePropertiesData(
      "cdiscControlTerm"
    );
    setTableColumns(data?.data || {});
  };

  useEffect(() => {
    getGlobalTablePropertiesData();
  }, []);

  const updateTableProperties = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: `You want to update table property!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#103C5E",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, Update it!`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        setButtonLoader(true);
        let userData = {};
        userData.type = "cdiscControlTerm";
        userData.columns = tableColumns;
        let data = await studyOtherSettingsServices.updateTableProperties(
          userData
        );
        if (data?.statusCode === 200) {
          getStudyCDISCTMap();
          getGlobalTablePropertiesData();
        }
      }
      setButtonLoader(false);
    });
  };

  return (
    <>
      <div
        className="justify-content-between toolbar border-bottom hstack p-1"
        style={{ height: 36 }}
      >
        <div className="hstack gap-1">
          <i
            title={isFormOpen ? "Close Form" : "Add TMap Metadata"}
            onClick={() => {
              setIsFormOpen(!isFormOpen);
              setTMapId("");
            }}
            className={`fa-solid ${
              isFormOpen ? "fa-table-list" : "fa-plus-square CP"
            }`}
          />
          <div className="vr"></div>
          {tMapMetadataCount > 0 && (
            <div className="position-relative">
              <Form.Control
                type="Search"
                onChange={(e) => setSearchData(e.target.value)}
                value={searchData}
                placeholder="Search..."
                style={{ width: 200, padding: "5px 10px" }}
              ></Form.Control>
              <CiSearch
                className="CP position-absolute bg-white"
                title="Search"
                style={{ width: 15, top: 5, right: 7 }}
              />
            </div>
          )}
        </div>
        <div className="hstack gap-2">
          <button
            onClick={() => toggles.setToggle4()}
            className={`updateProfile`}
            style={{ fontSize: 12 }}
          >
            Import
          </button>
          <button
            onClick={() => toggles.setToggle3()}
            className={`updateProfile`}
            style={{ fontSize: 12 }}
          >
            Pull Metadata
          </button>
          {tMapMetadataCount > 0 && (
            <button
              onClick={() => toggles.setToggle2()}
              className={`updateProfile`}
              style={{ fontSize: 12 }}
            >
              Table Properties
            </button>
          )}
          {deletedTMapCount > 0 && (
            <button
              onClick={() => toggles.setToggle1()}
              className={`updateProfile`}
            >
              Deleted History
            </button>
          )}
        </div>
      </div>
      <SplitPane
        split="horizontal"
        className="position-relative"
        style={{ height: "calc(100vh - 182px)" }}
        minSize={tMapId ? "40%" : "100%"}
      >
        <SplitPane
          split="vertical"
          minSize={20}
          className="position-relative"
          defaultSize={isFormOpen ? "20%" : "100%"}
        >
          {isFormOpen && (
            <AddNewTMapMetadata
              studyId={studyId}
              getStudyCDISCTMap={getStudyCDISCTMap}
            />
          )}
          {loader ? (
            <PageLoader />
          ) : tMapMetadata?.length === 0 ? (
            <NoDataFoundTable MSG={"TMap metadata not found!"} />
          ) : (
            <>
              <Table
                ref={tableRef}
                className="m-0 custom-table custom-tables"
                hover
                striped
              >
                <thead className="position-sticky top-0">
                  <tr>
                    <th
                      onMouseDown={(e) => onMouseDown(e, 0)}
                      style={{ width: 45 }}
                      className="text-center"
                    >
                      Sr.
                    </th>
                    {!tableColumns["DOMAIN"] && (
                      <th onMouseDown={(e) => onMouseDown(e, 1)}>Domain</th>
                    )}
                    {!tableColumns["PATTERN"] && (
                      <th onMouseDown={(e) => onMouseDown(e, 2)}>Pattern</th>
                    )}
                    {!tableColumns["TESTCD"] && (
                      <th onMouseDown={(e) => onMouseDown(e, 3)}>Test CD</th>
                    )}
                    {!tableColumns["TESTNAME"] && (
                      <th onMouseDown={(e) => onMouseDown(e, 4)}>Test Name</th>
                    )}
                    {!tableColumns["CREATED"] && (
                      <th
                        onMouseDown={(e) => onMouseDown(e, 5)}
                        style={{ width: 180 }}
                      >
                        Created Date | Time
                      </th>
                    )}
                    {!tableColumns["UPDATED"] && (
                      <th
                        onMouseDown={(e) => onMouseDown(e, 6)}
                        style={{ width: 180 }}
                      >
                        Updated Date | Time
                      </th>
                    )}
                    {!tableColumns["EDIT"] && (
                      <th
                        onMouseDown={(e) => onMouseDown(e, 7)}
                        style={{ width: 45 }}
                        className="text-center"
                      >
                        Edit
                      </th>
                    )}
                    {!tableColumns["DELETE"] && (
                      <th
                        onMouseDown={(e) => onMouseDown(e, 8)}
                        style={{ width: 55 }}
                        className="text-center"
                      >
                        Delete
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {tMapMetadata?.map((item, index) => (
                    <tr
                      key={index}
                      onClick={() => {
                        setIsFormOpen(false);
                        setTMapId(item?.tMapId);
                        setTMapData(item);
                      }}
                      className={`CP ${
                        tMapId === item?.tMapId ? "highlighted" : ""
                      }`}
                      title="View Details"
                    >
                      <td className="text-center">{startIndex + index + 1}</td>
                      {!tableColumns["DOMAIN"] && <td>{item.domain}</td>}
                      {!tableColumns["PATTERN"] && <td>{item.pattern}</td>}
                      {!tableColumns["TESTCD"] && <td>{item.testCD}</td>}
                      {!tableColumns["TESTNAME"] && <td>{item.testName}</td>}
                      {!tableColumns["CREATED"] && (
                        <td>
                          <Moment format="DD MMM YYYY | HH:mm" withTitle>
                            {item.createdAt}
                          </Moment>
                        </td>
                      )}
                      {!tableColumns["UPDATED"] && (
                        <td>
                          <Moment format="DD MMM YYYY | HH:mm" withTitle>
                            {item.updatedAt}
                          </Moment>
                        </td>
                      )}
                      {!tableColumns["EDIT"] && (
                        <td
                          className="text-center CP"
                          title="Update"
                          onClick={(e) => {
                            updateTMapModal(item);
                            e.stopPropagation();
                          }}
                        >
                          <FaEdit className="text-muted" />
                        </td>
                      )}
                      {!tableColumns["DELETE"] && (
                        <td
                          className="text-center CP"
                          title="Delete"
                          onClick={(e) => {
                            softDeleteRestoreStudyCDISCTMaps(
                              item._id,
                              "delete"
                            );
                            e.stopPropagation();
                          }}
                        >
                          <FaTrash className="text-danger" />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="position-sticky bottom-0 start-0">
                <Pagination
                  totalPage={totalPage}
                  pageNumber={pageNumber}
                  itemsPerPage={itemsPerPage}
                  totalItems={tMapMetadataCount}
                  setPageNumber={setPageNumber}
                  setItemsPerPage={setItemsPerPage}
                />
              </div>
            </>
          )}
        </SplitPane>
        {tMapId && (
          <div className={isFullScreen ? "full-screen-component" : ""}>
            <div className="hstack paginate fixed_tab_button justify-content-between pt-1 border-bottom bg-light">
              <div className="hstack me-1 gap-1">
                {["activities"].map((tab) => (
                  <button
                    key={tab}
                    className={`buttonForTabs px-2 ${
                      activeTab === tab ? "activee" : ""
                    }`}
                    onClick={() => setActiveTab(tab)}
                    title={tab.charAt(0).toUpperCase() + tab.slice(1)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
              <div className="d-flex gap-1">
                <div className="toolbar">
                  <i
                    className="fa-solid fa-angle-down"
                    onClick={() => setTMapId(false)}
                  ></i>
                </div>
                <div className="toolbar">
                  <i
                    className={`fa-solid me-2 ${
                      !isFullScreen ? "fa-maximize" : "fa-minimize"
                    }`}
                    title={!isFullScreen ? "Full Mode" : "Original Mode"}
                    onClick={() => setIsFullScreen(!isFullScreen)}
                  ></i>
                </div>
              </div>
            </div>
            {activeTab === "activities" && (
              <TMapMetadataActivities
                tableRef={tableRef}
                onMouseDown={onMouseDown}
                formActivity={formActivity}
                tMapData={tMapData}
                activityLoader={activityLoader}
                formActivityCount={formActivityCount}
                getStudyCDISCAuditTrials={getStudyCDISCAuditTrials}
              />
            )}
          </div>
        )}
      </SplitPane>
      <UpdateTMapMetadata
        studyId={studyId}
        tMapData={tMapData}
        getStudyCDISCTMap={getStudyCDISCTMap}
        Show={!toggles.toggle}
        Hide={toggles.setToggle}
        Title={"Update TMaps"}
      />
      <DeletedTMapMetadata
        Show={!toggles.toggle1}
        Hide={toggles.setToggle1}
        setTMapId={setTMapId}
        tableRef={tableRef}
        onMouseDown={onMouseDown}
        deletedTMap={deletedTMap}
        getDeletedTMapList={getDeletedTMapList}
        deletedTMapCount={deletedTMapCount}
        pageNumberDeleted={pageNumberDeleted}
        itemsPerPageDeleted={itemsPerPageDeleted}
        setPageNumberDeleted={setPageNumberDeleted}
        setItemsPerPageDeleted={setItemsPerPageDeleted}
        softDeleteRestoreStudyCDISCTMaps={softDeleteRestoreStudyCDISCTMaps}
        Title={"Deleted TMaps History"}
      />
      <TableProperties
        Show={!toggles.toggle2}
        Hide={toggles.setToggle2}
        Title={"Table Properties"}
        tableRef={tableRef}
        onMouseDown={onMouseDown}
        formColumn={formColumn}
        tableColumns={tableColumns}
        buttonLoader={buttonLoader}
        setTableColumns={setTableColumns}
        updateTableProperties={updateTableProperties}
      />

      <PullTMapMetadata
        Show={!toggles.toggle3}
        Hide={toggles.setToggle3}
        studyId={studyId}
        tableRef={tableRef}
        onMouseDown={onMouseDown}
        getStudyCDISCTMap={getStudyCDISCTMap}
        Title="Pull TMaps"
      />
      <ImportTMapData
        Show={!toggles.toggle4}
        Hide={toggles.setToggle4}
        Title={"Import TMap Data"}
        studyId={studyId}
        getStudyCDISCTMap={getStudyCDISCTMap}
        downloadTMapTemplate={downloadTMapTemplate}
      />
    </>
  );
};

export default TMapManagement;
